import { useEffect, useState, useCallback } from 'react';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { DownOutlined } from '@ant-design/icons';

import PageHeader from '../../uiKitComponents/pageHeader';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';
import Table from '../../uiKitComponents/table';
import Dropdown from '../../uiKitComponents/dropdown';
import FiltersSection from '../../components/filtersSection';
import PaginationSummary from '../../components/PaginationSummary';

import {
  getCrowdEvidenceImpactReport,
  cleanReports,
  setCleanOnUnmountTrue as setReportsCleanOnUnmountTrue,
} from '../../store/actions/reportPagesAction';
import { getUserFilters } from '../../store/actions/userFiltersAction';
import buildTableColumns from '../../utils/buildTableColumns';
import formattingFilters from '../../utils/formattingFilters';

const initialState = {
  page: 1,
  sort_column: 'id',
  sort_direction: 'descend',
  filters: [{}],
  keyword: undefined,
  filter_info: undefined,
};

const CrowdEvidenceImpactPage = () => {
  const [state, setState] = useSessionStorage('crowd-evidence-impact', {
    ...initialState,
  });
  const {
    page,
    sort_column,
    sort_direction,
    filters,
    keyword,
    filter_info,
  } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { cleanOnUnmount, crowdEvidenceImpact, savedFilters } = useSelector(
    (store) => ({
      cleanOnUnmount: store.reportPages.cleanOnUnmount,
      crowdEvidenceImpact: store.reportPages.payload.crowdEvidenceImpact,
      savedFilters: store.userFilters.payload,
    }),
  );

  useMount(() => {
    setReportsCleanOnUnmountTrue()(dispatch);
  });

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('crowd-evidence-impact');
      cleanReports()(dispatch);
    }
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    const filterParams = {};
    filters.forEach((filter) =>
      Object.entries(
        formattingFilters(filter, filter.keyword, 'STRING'),
      ).forEach(([key, value]) => {
        filterParams[`filters[${key}]`] = value;
      }),
    );

    getCrowdEvidenceImpactReport({
      page,
      sort_column,
      sort_direction: sort_direction || 'ascend',
      keyword,
      ...filterParams,
    })(dispatch)
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('crowd-evidence-impact');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, page, sort_column, sort_direction, keyword, filters]);

  useEffect(() => {
    fetchData();
  }, [dispatch, fetchData]);

  const fetchFilters = useCallback(() => {
    getUserFilters('crowd-evidence-impact')(dispatch).catch((err) => {
      if (err.response?.status === 500) {
        setError({
          status: err.response.status,
          message: err.response.data.message,
        });
      }
    });
  }, [dispatch, setError]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  const selectSavedFilter = (filterId) => {
    const id = Number(filterId);
    const filter = savedFilters.find((f) => f.id === id);
    if (filter) {
      const newFilters = Object.entries(filter.filters).map(([key, value]) =>
        formattingFilters(key, value, 'OBJECT'),
      );
      setState((prevState) => ({
        ...prevState,
        filters: newFilters,
        keyword: filter.keyword,
        filter_info: filter,
      }));
    }
  };

  const onSubmitFilters = (values) =>
    setState((prevState) => ({
      ...prevState,
      ...values,
    }));

  const clearFilters = () => setState(initialState);

  const onTableChange = useCallback(
    (pagination, _, sorter, { action }) => {
      const newState = {};
      if (action === 'paginate') {
        newState.page = pagination.current;
      }
      if (action === 'sort') {
        newState.sort_column = sorter.field;
        newState.sort_direction = sorter.order;
      }
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState],
  );

  if (error)
    return (
      <PageHeader className="page-main-header" title="Crowd Evidence Impact">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader
        className="page-main-header"
        title="Crowd Evidence Impact"
        extra={[
          <Dropdown
            menu={{
              items: savedFilters?.map((item) => ({
                label: item.name,
                key: item.id,
              })),
              onClick: ({ key }) => selectSavedFilter(key),
            }}
            trigger={['click']}
            placement="bottomRight"
            disabled={!savedFilters.length}
            key="saved_filters"
          >
            <Button loading={!savedFilters}>
              Saved Filters
              <DownOutlined />
            </Button>
          </Dropdown>,
        ]}
      >
        <FiltersSection
          clearFilters={clearFilters}
          onSubmit={onSubmitFilters}
          filters={filters}
          keyword={keyword}
          columnOptions={crowdEvidenceImpact?.filterable}
          loading={!crowdEvidenceImpact?.filterable}
          filter_info={filter_info}
          filter_columns={crowdEvidenceImpact?.columns}
          onUpdateFilter={selectSavedFilter}
          entity="crowd-evidence-impact"
        />
      </PageHeader>
      <div className="summary-container">
        {crowdEvidenceImpact?.volume_by_type?.map((item) => (
          <button
            type="button"
            key={item.type}
            className={'summary-el summary-el--read-only'}
          >
            <span className="text-capitalize">{item.type}: </span>
            <span>{item.total}</span>
          </button>
        ))}
        <button type="button" className={'summary-el summary-el--read-only'}>
          <span className="text-capitalize">Promotions Triggered: </span>
          <span>{crowdEvidenceImpact?.promotions_triggered}</span>
        </button>
        <button type="button" className={'summary-el summary-el--read-only'}>
          <span className="text-capitalize">Score Lift: </span>
          <span>{crowdEvidenceImpact?.score_lift}</span>
        </button>
      </div>
      <div className="page-main-content">
        <PaginationSummary meta={crowdEvidenceImpact?.meta} />
        <Table
          columns={buildTableColumns(crowdEvidenceImpact?.columns)}
          rowKey="country"
          dataSource={crowdEvidenceImpact?.data}
          loading={loading}
          onChange={onTableChange}
          pagination={{
            position: ['topRight', 'bottomRight'],
            showSizeChanger: false,
            showQuickJumper: true,
            pageSize: crowdEvidenceImpact?.meta?.per_page,
            total: crowdEvidenceImpact?.meta?.total,
            current: crowdEvidenceImpact?.meta?.current_page,
          }}
        />
        <PaginationSummary meta={crowdEvidenceImpact?.meta} />
      </div>
    </div>
  );
};

export default CrowdEvidenceImpactPage;
